import React, { useEffect, useState } from "react"
import {
    Box,
    componentStyles,
    HorizontalStackSpaceBetween,
    useTheme,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDollarSign } from "@fortawesome/free-solid-svg-icons/faDollarSign"

import { parseInteger } from "~utils/parsing-helpers"
import { TEST_IDS } from "~config/test-ids"
import COPY from "~config/copy-constants"
import {
    TextMedium,
    TextXLargeBold,
} from "~components/shared/todo-lib-react-components/text"
import Input from "~components/shared/todo-lib-react-components/input"
import Slider from "~components/shared/todo-lib-react-components/slider/slider"

import { useSharedStyles } from "./use-shared-styles"

export default function PriceRange({
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    shouldClear,
    setShouldClear,
}: {
    readonly priceMin: number | undefined
    readonly setPriceMin: (priceMin: number | undefined) => void
    readonly priceMax: number | undefined
    readonly setPriceMax: (priceMax: number | undefined) => void
    readonly shouldClear: boolean
    readonly setShouldClear: (shouldClear: boolean) => void
}) {
    const styles = useStyles()
    const sharedStyles = useSharedStyles()
    const min = 0
    const initialMax = 5000
    const [max, setMax] = useState<number>(
        Math.max(priceMax || initialMax, initialMax)
    )
    const [value, setValue] = useState<[number, number]>([
        priceMin || min,
        priceMax || (priceMin && priceMin > max ? priceMin + 100 : max),
    ])
    const [minString, setMinString] = useState<string>(value[0].toString())
    const [maxString, setMaxString] = useState<string>(value[1].toString())
    const [isMaxUncapped, setIsMaxUncapped] = useState<boolean>(!priceMax)

    useEffect(() => {
        if (shouldClear) {
            setValue([min, initialMax])
            setMinString(min.toString())
            setMaxString(initialMax.toString())
            setShouldClear(false)
            setMax(initialMax)
            setIsMaxUncapped(true)
        }
    }, [shouldClear])

    useEffect(() => {
        if (value[0] < 0) setValue([0, value[1]])
    }, [value[0]])

    useEffect(() => {
        if (value[1] < 0) setValue([value[0], 0])
    }, [value[1]])

    function onChangeSlider(newValue: [number, number]) {
        if (newValue[1] - newValue[0] < 100) {
            if (newValue[0] === value[0]) {
                newValue[1] = newValue[0] + 100
            } else {
                newValue[0] = newValue[1] - 100
            }
        }
        setValue(newValue)
        setMinString(newValue[0].toString())
        setMaxString(newValue[1].toString())
        if (isMaxUncapped) {
            setIsMaxUncapped(false)
        }
    }

    function onChangeMin(newVal: string) {
        const numberVal = newVal.replace(/\D/g, "") || "0"
        try {
            if (!numberVal) {
                setValue([min, value[1]])
            } else {
                const newMinValue = parseInteger(numberVal)

                if (newMinValue > value[1]) {
                    setValue([newMinValue, newMinValue + 50])
                    setMaxString((newMinValue + 50).toString())
                } else {
                    setValue([newMinValue, value[1]])
                }
            }
            setMinString(numberVal)
        } catch (error) {
            setMinString(numberVal)
        }
    }

    function onChangeMax(newVal: string) {
        const numberVal =
            newVal.replace(/\D/g, "") ||
            (() => {
                setIsMaxUncapped(true)
                return max.toString()
            })()
        try {
            if (!numberVal) {
                setValue([value[0], value[0]])
            } else {
                const newMaxValue = parseInteger(numberVal)

                setMax(Math.max(max, newMaxValue))

                if (isMaxUncapped) {
                    setIsMaxUncapped(false)
                } else if (newVal.includes("+")) {
                    setIsMaxUncapped(true)
                }

                if (newMaxValue < value[0]) {
                    setValue([value[0], value[0]])
                } else {
                    setValue([value[0], newMaxValue])
                }
            }
            setMaxString(numberVal)
        } catch (error) {
            setMaxString(numberVal)
        }
    }

    function checkValues() {
        const bottom = parseInteger(minString)
        const top = parseInteger(maxString)

        if (top < bottom) {
            setValue([top - 50 > min ? top - 50 : min, top])
            setMinString((top - 50 > min ? top - 50 : min).toString())
            setMaxString(top.toString())
        }
    }

    useEffect(() => {
        if (value[0] === min) {
            setPriceMin(undefined)
        } else {
            setPriceMin(value[0])
        }

        if (isMaxUncapped) {
            setPriceMax(undefined)
        } else {
            setPriceMax(value[1])
        }
    }, [value])

    return (
        <Box style={sharedStyles.wrapper}>
            <TextXLargeBold>{COPY.PRICE_RANGE}</TextXLargeBold>
            <TextMedium style={styles.helpText}>
                {COPY.SET_MIN_MAX_PRICE}
            </TextMedium>
            <Box style={styles.sliderWrapper}>
                <Box style={styles.sliderContainer}>
                    <Slider
                        min={min}
                        max={Math.max(max, value[1])}
                        value={value}
                        onChange={onChangeSlider}
                    />
                </Box>
                <HorizontalStackSpaceBetween style={styles.inputWrapper}>
                    <Box style={styles.inputContainer}>
                        <TextMedium style={styles.inputLabel}>Min</TextMedium>
                        <Input
                            startIcon={<FontAwesomeIcon icon={faDollarSign} />}
                            style={styles.input}
                            value={
                                parseInteger(minString).toLocaleString() || ""
                            }
                            onChange={(newValue) => onChangeMin(newValue)}
                            testId={TEST_IDS.MIN_PRICE_INPUT}
                        />
                    </Box>
                    <Box style={styles.divider}>-</Box>
                    <Box style={styles.inputContainer}>
                        <TextMedium style={styles.inputLabel}>Max</TextMedium>
                        <Input
                            startIcon={<FontAwesomeIcon icon={faDollarSign} />}
                            style={styles.input}
                            value={
                                isMaxUncapped
                                    ? `${parseInteger(
                                          maxString
                                      ).toLocaleString()}+`
                                    : parseInteger(
                                          maxString
                                      ).toLocaleString() || ""
                            }
                            onChange={(newValue) => onChangeMax(newValue)}
                            onBlur={checkValues}
                            testId={TEST_IDS.MAX_PRICE_INPUT}
                        />
                    </Box>
                </HorizontalStackSpaceBetween>
            </Box>
        </Box>
    )
}

function useStyles() {
    const { colors } = useTheme()

    return componentStyles({
        sliderWrapper: {
            paddingTop: "6px",
            paddingBottom: "24px",
        },
        sliderContainer: {
            marginX: "12px",
        },
        inputWrapper: {
            alignItems: "center",
        },
        inputContainer: {
            flex: 1,
        },
        inputLabel: {
            marginTop: 0,
            marginBottom: 0,
        },
        input: {
            backgroundColor: colors.bgColor,
        },
        divider: {
            fontSize: "1.5rem",
            marginX: "16px",
            paddingTop: "16px",
        },
        helpText: {
            fontWeight: 300,
            fontSize: { base: "0.9rem", md: "1rem" },
            color: colors.contentTertiary,
        },
    })
}
